import { FC, useMemo, useState } from 'react'
import { QrCode, Info, AlertCircle, MapPin, Package, Tag, Building2, ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { DataStructure } from '@/types/DataStructure'
import { AssetCore } from '@/types/structures'
import { decodeFloor } from '@/utils/windowPosition'

interface DisplayField {
  label: string
  value: string
  suffix?: string
}

interface DisplayFieldGroup {
  id: string
  label: string
  icon: string
  fields: DisplayField[]
}

interface QRInfoNode {
  id: string
  title: string
  description: string
  type: 'qr-info'
  icon: string
  displayFields?: DisplayFieldGroup[]
}

interface QRInfoCardProps {
  uuid: string
  asset?: DataStructure | null
  assetCore?: AssetCore | null
  project?: {
    uuid: string
    info: { identification?: string; subIdentification?: string }
  } | null
  isLoading?: boolean
  error?: string | null
  className?: string
}

const QRInfoCard: FC<QRInfoCardProps> = ({ uuid, asset, assetCore, project, isLoading = false, error = null, className = '' }) => {
  const { t } = useTranslation()
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) newSet.delete(itemId)
      else newSet.add(itemId)
      return newSet
    })
  }

  const qrInfoConfig = useMemo(() => {
    if (!asset?.nodes) return null
    const qrInfoNode = Object.values(asset.nodes).find(node => {
      const nodeWithType = node as unknown as { type?: string; id?: string }
      return nodeWithType.type === 'qr-info' || nodeWithType.id === 'qr-info'
    })
    if (qrInfoNode) {
      const nodeWithFields = qrInfoNode as unknown as { displayFields?: DisplayFieldGroup[] }
      if (nodeWithFields.displayFields) return nodeWithFields as QRInfoNode
      return {
        id: 'qr-info', title: 'QR_INFO_TITLE', description: 'QR_INFO_DESCRIPTION', type: 'qr-info' as const, icon: 'qrcode',
        displayFields: [
          { id: 'position', label: 'QR_POSITION_LABEL', icon: 'mappin', fields: [
            { label: 'POSITION_NUMBER_LABEL', value: '#/values/positionInfo/number' },
            { label: 'POSITION_SEQUENCE_LABEL', value: '#/values/positionInfo/sequence' },
            { label: 'FLOOR_LABEL', value: '#/values/positionInfo/floor' },
            { label: 'POSITION_DESCRIPTION_LABEL', value: '#/values/positionInfo/description' },
          ]},
          { id: 'serramento', label: 'QR_SERRAMENTO_LABEL', icon: 'info', fields: [
            { label: 'BRAND_LABEL', value: '#/values/productDetail/brand' },
            { label: 'MODEL_LABEL', value: '#/values/productDetail/model' },
            { label: 'MODEL_DESCRIPTION_LABEL', value: '#/values/productDetail/description' },
            { label: 'ARTICLE_DESCRIPTION_LABEL', value: '#/values/positionInfo/articleDescription' },
            { label: 'WIDTH_LABEL', value: '#/values/positionInfo/width', suffix: 'mm' },
            { label: 'HEIGHT_LABEL', value: '#/values/positionInfo/height', suffix: 'mm' },
          ]},
        ]
      } as QRInfoNode
    }
    return qrInfoNode
  }, [asset])

  const getFieldValue = (reference: string): string => {
    if (!reference.startsWith('#/values/')) return ''
    const cleanPath = reference.replace('#/values/', '')
    const flattenedKey = cleanPath.replace(/\//g, '.')
    for (const node of Object.values(asset?.nodes || {})) {
      if (node.children) {
        for (const child of node.children) {
          if (child.path === reference && child.value !== reference && child.value !== '-') return child.value || ''
          if (child.path) {
            const childFlattenedKey = child.path.replace('#/values/', '').replace(/\//g, '.')
            if (childFlattenedKey === flattenedKey && child.value !== child.path && child.value !== '-') return child.value || ''
          }
        }
      }
    }
    return ''
  }

  if (isLoading) {
    return (
      <div className={`rounded-xl bg-[var(--brand-dark)] p-5 ${className}`}>
        <div className="flex items-center gap-4">
          <div className="animate-pulse p-3 bg-white/10 rounded-lg"><QrCode className="h-8 w-8 text-white/30" /></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-white/10 rounded animate-pulse" />
            <div className="h-3 bg-white/10 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-white/10 rounded animate-pulse w-1/2" />
          </div>
        </div>
        <div className="mt-4 text-sm text-white/40 text-center">{t('QR_LOADING')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`rounded-xl border border-[hsl(var(--destructive))]/20 bg-[hsl(var(--card))] p-5 ${className}`}>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-[hsl(var(--destructive))]/10 rounded-lg"><AlertCircle className="h-8 w-8 text-[hsl(var(--destructive))]" /></div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">{t('QR_INFO_TITLE')}</h3>
            <div className="flex items-center gap-2">
              <Tag className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('QR_TAG_ID_LABEL')}:</span>
              <span className="text-xs font-mono font-medium break-all">{uuid}</span>
            </div>
            <div className="mt-2 text-sm text-[hsl(var(--destructive))]">{error}</div>
          </div>
        </div>
      </div>
    )
  }

  if (!qrInfoConfig || !qrInfoConfig.displayFields) return null

  const positionGroup = qrInfoConfig.displayFields.find(g => g.id === 'position')
  const serramentoGroup = qrInfoConfig.displayFields.find(g => g.id === 'serramento')

  const positionNumber = positionGroup ? getFieldValue(positionGroup.fields.find(f => f.label === 'POSITION_NUMBER_LABEL')?.value || '') : ''
  const positionDescription = positionGroup ? getFieldValue(positionGroup.fields.find(f => f.label === 'POSITION_DESCRIPTION_LABEL')?.value || '') : ''
  const floor = positionGroup ? getFieldValue(positionGroup.fields.find(f => f.label === 'FLOOR_LABEL')?.value || '') : ''

  const brand = serramentoGroup ? getFieldValue(serramentoGroup.fields.find(f => f.label === 'BRAND_LABEL')?.value || '') : ''
  const model = serramentoGroup ? getFieldValue(serramentoGroup.fields.find(f => f.label === 'MODEL_LABEL')?.value || '') : ''
  const modelDescription = serramentoGroup ? getFieldValue(serramentoGroup.fields.find(f => f.label === 'MODEL_DESCRIPTION_LABEL')?.value || '') : ''
  const articleDescription = serramentoGroup ? getFieldValue(serramentoGroup.fields.find(f => f.label === 'ARTICLE_DESCRIPTION_LABEL')?.value || '') : ''
  const width = serramentoGroup ? getFieldValue(serramentoGroup.fields.find(f => f.label === 'WIDTH_LABEL')?.value || '') : ''
  const height = serramentoGroup ? getFieldValue(serramentoGroup.fields.find(f => f.label === 'HEIGHT_LABEL')?.value || '') : ''

  const productName = [brand, model].filter(Boolean).join(' ')
  const productSubtitle = [modelDescription, articleDescription].filter(Boolean).join(' — ')

  return (
    <div className={`rounded-xl bg-[var(--brand-dark)] p-5 relative overflow-hidden ${className}`}>
      <div className="absolute top-0 right-0 w-32 h-32" style={{ background: 'radial-gradient(circle at top right, rgba(249,56,34,0.12) 0%, transparent 70%)' }} />

      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          {positionNumber && (
            <span className="bg-[var(--brand-red)]/20 text-[var(--brand-red)] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
              {t('LINKING_POSITION_NUMBER')} {positionNumber}
            </span>
          )}
          {floor && <span className="text-[10px] text-[var(--brand-grey)]">{decodeFloor(floor)}</span>}
          {positionDescription && <span className="text-[10px] text-[var(--brand-grey)]">{positionDescription}</span>}
        </div>

        {productName && (
          <h3 className="text-[17px] font-bold text-white leading-tight">{productName}</h3>
        )}
        {productSubtitle && (
          <p className="text-xs text-[var(--brand-blue)] mt-1">{productSubtitle}</p>
        )}

        {(width || height) && (
          <div className="flex items-center gap-3 mt-3">
            {width && (
              <div>
                <div className="text-[9px] text-[var(--brand-grey)] uppercase tracking-wider">Larghezza</div>
                <div className="text-sm font-semibold text-white">{width}<span className="text-[10px] text-[var(--brand-grey)] ml-0.5">mm</span></div>
              </div>
            )}
            {width && height && <div className="w-px h-8 bg-white/10" />}
            {height && (
              <div>
                <div className="text-[9px] text-[var(--brand-grey)] uppercase tracking-wider">Altezza</div>
                <div className="text-sm font-semibold text-white">{height}<span className="text-[10px] text-[var(--brand-grey)] ml-0.5">mm</span></div>
              </div>
            )}
          </div>
        )}

        {!productName && !positionNumber && !width && !height && (
          <div className="space-y-3">
            {qrInfoConfig.displayFields.map(fieldGroup => {
              const fieldValues = fieldGroup.fields
                .map(field => ({ ...field, currentValue: getFieldValue(field.value) }))
                .filter(field => field.currentValue)
              if (fieldValues.length === 0) return null
              const displayText = fieldValues.map(f => `${f.currentValue}${f.suffix ? ` ${f.suffix}` : ''}`).join(' • ')
              const itemId = fieldGroup.id
              const isExpanded = expandedItems.has(itemId)
              const shouldTruncate = displayText.length > 50

              return (
                <div key={fieldGroup.id} className="p-2.5 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-[var(--brand-grey)]">{t(fieldGroup.label)}</div>
                    <div className={`flex-1 text-sm font-medium text-white ${!isExpanded && shouldTruncate ? 'truncate' : ''}`}>
                      {displayText}
                    </div>
                    {shouldTruncate && (
                      <button onClick={() => toggleExpanded(itemId)} className="flex-shrink-0 p-1 hover:bg-white/10 rounded transition-colors">
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-white/40" /> : <ChevronDown className="h-3.5 w-3.5 text-white/40" />}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default QRInfoCard
export type { QRInfoCardProps }
